import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { AutomacoesRequest } from '@shared/services/requests/compras/automacoes.request';
import { AutomacaoStruct } from '@shared/services/structs/compras/automacao.struct';
import { GetAutomacoesResponse } from '@shared/services/responses/compras/get-automacoes.response';
import { SalvarAutomacoesResponse } from '@shared/services/responses/compras/salvar-automacoes.response';
import { TipoAutomacaoEnum } from '@shared/enums/compras/tipo-automacao.enum';
import { TipoDestinatarioNotificacaoEnum } from '@shared/enums/compras/tipo-destinatario-notificacao.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { PerfilService } from '../usuario/perfil.service';

@Injectable({
  providedIn: 'root',
})
export class AutomacaoService extends CommonService {
  private readonly apiUrl: string = environment.urlApiCompras + 'Automacao';

  /**
   * Cache da configuração para os disparos no quadro: o processo de compras consulta
   * a cada fornecedor adicionado / pedido aprovado, e a config muda raramente.
   * `salvar` invalida.
   */
  private cache$?: Observable<AutomacaoStruct[]>;

  constructor(
    private httpClient: HttpClient,
    private perfilService: PerfilService,
  ) {
    super();
  }

  public get(): Observable<GetAutomacoesResponse> {
    return this.httpClient
      .get<GetAutomacoesResponse>(this.apiUrl, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvar(request: AutomacoesRequest): Observable<SalvarAutomacoesResponse> {
    return this.httpClient
      .put<SalvarAutomacoesResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(
        tap(() => this.invalidarCache()),
        catchError(this.handleError),
      );
  }

  private invalidarCache(): void {
    this.cache$ = undefined;
  }

  /**
   * Automação ativa do tipo informado, ou null. Erro de rede/permissão devolve null:
   * automação é acessório do fluxo, não pode derrubar a ação que o usuário pediu.
   */
  public automacaoAtiva(tipo: TipoAutomacaoEnum): Observable<AutomacaoStruct | null> {
    return this.automacoes().pipe(
      map(automacoes => automacoes.find(a => a.idTipoAutomacao === tipo && a.isAtivo) ?? null),
    );
  }

  /**
   * Expande em ids de usuário os perfis configurados como destinatário das notificações
   * ativas do gatilho. O backend não faz isso porque perfil -> usuários vive na API de
   * Usuário; ele só usa esses ids quando de fato existe destinatário do tipo Perfil.
   *
   * Sem perfil configurado, resolve com lista vazia sem chamar ninguém — o caminho comum
   * não paga nada por isso.
   */
  public idsUsuarioDosPerfis(gatilho: TipoOcorrenciaCompraEnum): Observable<number[]> {
    return this.automacoes().pipe(
      switchMap(automacoes => {
        const idsPerfil = [...new Set(
          automacoes
            .filter(a => a.idTipoAutomacao === TipoAutomacaoEnum.Notificar
              && a.isAtivo
              && a.idTipoOcorrenciaCompra === gatilho)
            .flatMap(a => a.destinatarios || [])
            .filter(d => d.idTipoDestinatarioNotificacao === TipoDestinatarioNotificacaoEnum.Perfil)
            .map(d => d.idPerfil)
            .filter((id): id is number => !!id),
        )];

        if (!idsPerfil.length) return of([] as number[]);

        return this.perfilService.getUsuariosPorPerfil(idsPerfil).pipe(
          map(res => (res.isError ? [] : [...new Set((res.lista || []).map(v => v.idUsuario))])),
          catchError(() => of([] as number[])),
        );
      }),
    );
  }

  /**
   * Só o sucesso entra no cache. Antes o `catchError` ficava antes do `shareReplay`, então uma
   * falha transitória no primeiro disparo memorizava "nenhuma automação" pela sessão inteira —
   * cotação deixava de sugerir o formulário e o pedido de ser enviado, sem erro visível. Agora a
   * falha resolve como lista vazia apenas para quem estava esperando, e o cache é solto para a
   * próxima chamada tentar de novo.
   */
  private automacoes(): Observable<AutomacaoStruct[]> {
    if (!this.cache$) {
      this.cache$ = this.get().pipe(
        map(res => {
          if (res.isError) throw new Error(res.errorDescription || 'Não foi possível carregar as automações.');
          return res.automacoes || [];
        }),
        catchError(() => {
          this.invalidarCache();
          return of([] as AutomacaoStruct[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.cache$;
  }
}
